import React, { useEffect, useState } from 'react';
import hdLogo from '../assets/hd_logo.png';
import { AppleLogo, ArrowRightIcon, GoogleGLogo, XIcon } from './AppIcons';

const proofItems = [
  'Mood-first sessions',
  '15-180 min dials',
  'Focus ledger',
  'Available on iOS & Android',
];

const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/hustle-dial/id6748039370';
const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.hustledial.hustledial';
const SESSION_SECONDS = 3 * 60;
const RING_RADIUS = 92;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const formatSessionTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const HeroSection = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setElapsedSeconds((seconds) => (seconds >= SESSION_SECONDS ? 0 : seconds + 1));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, []);

  const remainingSeconds = SESSION_SECONDS - elapsedSeconds;
  const elapsedProgress = elapsedSeconds / SESSION_SECONDS;
  const elapsedPercent = Math.round(elapsedProgress * 100);
  const ringOffset = RING_CIRCUMFERENCE * (1 - elapsedProgress);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeMenuAndScroll = (id) => {
    setIsMobileMenuOpen(false);
    window.setTimeout(() => scrollToSection(id), 0);
  };

  return (
    <section className="hd-hero relative min-h-screen overflow-hidden">
      <nav className="hd-nav fixed top-0 left-0 right-0 z-50">
        <div className="hd-nav-inner">
          <a href="/" className="hd-nav-brand" aria-label="Hustle Dial home">
            <img src={hdLogo} alt="" className="hd-nav-logo" />
            <span>Hustle Dial</span>
          </a>

          <div className="hd-nav-links">
            <button type="button" onClick={() => scrollToSection('features')}>
              Features
            </button>
            <button type="button" onClick={() => scrollToSection('benefits')}>
              Benefits
            </button>
            <button type="button" onClick={() => scrollToSection('download')}>
              Download
            </button>
          </div>

          <button
            type="button"
            onClick={() => scrollToSection('download')}
            className="hd-nav-cta"
          >
            Download
          </button>

          <button
            className="hd-menu-button"
            type="button"
            onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
            aria-label="Open menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span className={isMobileMenuOpen ? 'is-open' : ''}></span>
            <span className={isMobileMenuOpen ? 'is-open' : ''}></span>
            <span className={isMobileMenuOpen ? 'is-open' : ''}></span>
          </button>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="hd-mobile-menu fixed inset-0 z-[9999]">
          <button
            className="hd-mobile-close"
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <XIcon className="w-8 h-8" />
          </button>
          <button type="button" onClick={() => closeMenuAndScroll('features')}>
            Features
          </button>
          <button type="button" onClick={() => closeMenuAndScroll('benefits')}>
            Benefits
          </button>
          <button type="button" onClick={() => closeMenuAndScroll('download')}>
            Download
          </button>
        </div>
      )}

      <div className="hd-hero-grid container mx-auto px-6 relative z-10">
        <div className="hd-hero-copy">
          <p className="hd-kicker text-lime">Deep Work Mode</p>
          <h1 className="hd-display text-bone leading-[0.88] mt-5">
            Stay in
            <span className="block text-lime italic">the flow.</span>
          </h1>
          <p className="hd-hero-subhead">
            Choose a mood, set the pace, and let Hustle Dial keep every focus
            session visible, intentional, and logged.
          </p>

          <div className="hd-hero-actions">
            <button
              type="button"
              onClick={() => scrollToSection('download')}
              className="hd-pill hd-pill-lime"
            >
              Download the App
              <ArrowRightIcon />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('features')}
              className="hd-pill hd-pill-ghost"
            >
              See the Flow
            </button>
          </div>

          <div className="hd-proof-row" aria-label="Product highlights">
            {proofItems.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>

        <div className="hd-hero-visual" aria-label="Hustle Dial focus session preview">
          <div className="hd-hero-phone">
            <div className="hd-hero-phone-notch"></div>
            <div className="hd-phone-screen">
              <div className="hd-phone-status">
                <span>SESSION 01 / BRAINSTORM</span>
                <button type="button" aria-label="Close preview">
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="hd-session-tag">
                <span></span>
                in flow
              </div>

              <div className="hd-timer-wrap">
                <svg viewBox="0 0 220 220" className="hd-timer-ring" aria-hidden="true">
                  <circle className="hd-ring-track" cx="110" cy="110" r="92" />
                  <circle
                    className="hd-ring-progress"
                    cx="110"
                    cy="110"
                    r={RING_RADIUS}
                    style={{
                      strokeDasharray: RING_CIRCUMFERENCE,
                      strokeDashoffset: ringOffset,
                    }}
                  />
                  <g className="hd-ring-ticks">
                    {Array.from({ length: 12 }).map((_, index) => (
                      <line
                        key={index}
                        x1="110"
                        y1="15"
                        x2="110"
                        y2="28"
                        transform={`rotate(${index * 30} 110 110)`}
                      />
                    ))}
                  </g>
                </svg>
                <div className="hd-timer-content">
                  <p>Remaining</p>
                  <strong>{formatSessionTime(remainingSeconds)}</strong>
                  <span>
                    {elapsedPercent}% - {formatSessionTime(elapsedSeconds)} elapsed
                  </span>
                </div>
              </div>

              <blockquote className="hd-quote-card">
                Write the messy version. That is the point.
              </blockquote>

              <div className="hd-session-controls">
                <button type="button" className="hd-control-pause">
                  Pause
                </button>
                <button type="button" className="hd-control-stop" aria-label="Stop session">
                  <span></span>
                </button>
              </div>

              <div className="hd-session-meta">
                <span>Plan - 3:00</span>
                <span>Break - off</span>
              </div>
            </div>
          </div>

          <div className="hd-hero-store-links" aria-label="Download Hustle Dial">
            <a
              href={IOS_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hd-hero-store-link"
              aria-label="Download Hustle Dial on the iOS App Store"
            >
              <AppleLogo className="w-6 h-6" />
            </a>
            <a
              href={ANDROID_PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hd-hero-store-link"
              aria-label="Download Hustle Dial on Google Play"
            >
              <GoogleGLogo className="w-6 h-6" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
